import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { fetchActiveCourses, fetchDemoRequest, fetchMyEnrollments } from "./clientCourseService";
import CourseCard from "./CourseCard";
import DemoRequestModal from "./DemoRequestModal";
import "./courses.css";
import "./coursesSection.css";

const CoursesSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoCourseId, setDemoCourseId] = useState(null);
  const [demoCourse, setDemoCourse] = useState(null);
  const [bookedCourseIds, setBookedCourseIds] = useState(new Set());
  const [activeBatchCountMap, setActiveBatchCountMap] = useState({});
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 280, behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetchActiveCourses()
      .then((res) => setCourses(res.data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user || courses.length === 0) return;

    Promise.allSettled([
      Promise.allSettled(courses.map((c) => fetchDemoRequest(c.id))),
      fetchMyEnrollments(),
    ]).then(([demoRes, enrollRes]) => {
      if (demoRes.status === "fulfilled") {
        const booked = new Set();
        demoRes.value.forEach((r, i) => {
          if (r.status === "fulfilled") booked.add(courses[i].id);
        });
        setBookedCourseIds(booked);
      }

      if (enrollRes.status === "fulfilled") {
        const countMap = {};
        (enrollRes.value.data || []).forEach((e) => {
          if (e.status === "ACTIVE") {
            countMap[e.courseId] = (countMap[e.courseId] || 0) + 1;
          }
        });
        setActiveBatchCountMap(countMap);
      }
    });
  }, [user, courses]);

  const handleDemo = (courseId) => {
    if (!user) { navigate("/login?redirect=/courses"); return; }
    setDemoCourse(courses.find((c) => c.id === courseId));
    setDemoCourseId(courseId);
  };

  if (loading) return (
    <section className="cs-section">
      <div className="cc-loading"><div className="cc-spinner" /></div>
    </section>
  );

  if (courses.length === 0) return (
    <section className="cs-section">
      <div className="cs-header">
        <div>
          <h2 className="cs-title">📚 Structured Courses</h2>
          <p className="cs-subtitle">Intensive live Zoom programs with daily sessions, personal coaching &amp; assessments</p>
        </div>
      </div>
      <div className="cs-coming-soon">
        <span className="cs-coming-soon-icon">🚀</span>
        <p>Exciting courses are coming soon. Stay tuned!</p>
      </div>
    </section>
  );

  return (
    <section className="cs-section">
      <div className="cs-header">
        <div>
          <h2 className="cs-title">📚 Structured Courses</h2>
          <p className="cs-subtitle">Intensive live Zoom programs with daily sessions, personal coaching &amp; assessments</p>
        </div>
      </div>

      <div className="cs-scroll-container">
        <button className="cs-arrow cs-arrow-left" onClick={() => scroll(-1)}>&#8249;</button>
        <div className="cs-scroll-wrap" ref={scrollRef}>
          {courses.map((c) => (
            <div key={c.id} className="cs-scroll-item">
              <CourseCard
                course={c}
                onView={(id) => navigate(`/courses/${id}`)}
                onDemo={handleDemo}
                demoBooked={bookedCourseIds.has(c.id)}
                activeBatchCount={activeBatchCountMap[c.id] || 0}
                onContinue={() => navigate("/my-courses")}
              />
            </div>
          ))}
        </div>
        <button className="cs-arrow cs-arrow-right" onClick={() => scroll(1)}>&#8250;</button>
      </div>

      <div className="cs-footer">
        <button className="cs-view-all-btn-full" onClick={() => navigate("/courses")}>
          Explore All Courses →
        </button>
      </div>

      {demoCourseId && (
        <DemoRequestModal
          courseId={demoCourseId}
          course={demoCourse}
          onSuccess={() => {
            setBookedCourseIds((prev) => new Set([...prev, demoCourseId]));
            setDemoCourseId(null);
            setDemoCourse(null);
          }}
          onClose={() => { setDemoCourseId(null); setDemoCourse(null); }}
        />
      )}
    </section>
  );
};

export default CoursesSection;
